import React from 'react';
import { Modal } from 'react-bootstrap';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { Typography } from 'Components/atoms';
import { useI18n } from 'Hooks/useI18n';
import {
    VIEW_COND_MODAL_TITLE,
    VIEW_COND_FIELD_PATH,
    VIEW_COND_OPERATOR,
    VIEW_COND_VALUE_LABEL,
    VIEW_COND_COMPOSITE,
    VIEW_COND_RAW_JSON,
} from 'Core-utils/constants/constants';
import { ConditionGroupPayload, RuleCondition, RuleDto } from 'Core-utils/types/rules.types';

interface ViewConditionsModalProps {
    rule: RuleDto | null;
    onClose: () => void;
}

type TFn = (k: string, f?: string) => string;

// ── Single condition card ──────────────────────────────────────────────────────
const ConditionCard = ({ cond, index, t }: { cond: RuleCondition; index: number; t: TFn }) => {
    const valStr = typeof cond.value === 'object' ? JSON.stringify(cond.value) : String(cond.value ?? '');
    return (
        <div className="view-conditions-modal__condition-card">
            <div className="d-flex justify-content-between align-items-center mb-3 view-conditions-modal__condition-card-header">
                <span className="view-conditions-modal__condition-badge">Condition {index + 1}</span>
                <code className="view-conditions-modal__type-tag">{cond.type}</code>
            </div>
            <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center view-conditions-modal__detail-row">
                    <span className="view-conditions-modal__detail-label">{t(VIEW_COND_FIELD_PATH, 'Field Path:')} </span>
                    <code className="view-conditions-modal__detail-value view-conditions-modal__detail-value--path">{cond.name}</code>
                </div>
                <div className="d-flex align-items-center view-conditions-modal__detail-row">
                    <span className="view-conditions-modal__detail-label">{t(VIEW_COND_OPERATOR, 'Operator:')} </span>
                    <span className="view-conditions-modal__detail-value view-conditions-modal__detail-value--operator">{cond.operator}</span>
                </div>
                {cond.value !== undefined && cond.value !== null && (
                    <div className="d-flex align-items-center view-conditions-modal__detail-row">
                        <span className="view-conditions-modal__detail-label">{t(VIEW_COND_VALUE_LABEL, 'Value:')} </span>
                        <span className="view-conditions-modal__detail-value view-conditions-modal__detail-value--value">{valStr}</span>
                    </div>
                )}
                {cond.composite_type && (
                    <div className="d-flex align-items-center view-conditions-modal__detail-row">
                        <span className="view-conditions-modal__detail-label">{t(VIEW_COND_COMPOSITE, 'Composite:')} </span>
                        <span className="view-conditions-modal__detail-value view-conditions-modal__detail-value--composite">
                            {cond.composite_type.toUpperCase()}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Recursive group renderer ───────────────────────────────────────────────────
// Returns the rendered node and the next available condition index so sequential
// numbering is correct across nested groups without relying on module-level state.
function renderGroup(
    group: ConditionGroupPayload,
    depth: number,
    startIdx: number,
    t: TFn,
): { node: React.ReactNode; nextIdx: number } {
    let idx = startIdx;

    const condNodes = (group.conditions ?? []).map((c) => {
        const cardIdx = idx;
        idx += 1;
        return <ConditionCard key={`cond-${cardIdx}`} cond={c as RuleCondition} index={cardIdx} t={t} />;
    });

    const childNodes: React.ReactNode[] = [];
    (group.childGroups ?? []).forEach((child, i) => {
        const result = renderGroup(child, depth + 1, idx, t);
        childNodes.push(<React.Fragment key={`child-${i}`}>{result.node}</React.Fragment>);
        idx = result.nextIdx;
    });

    const logicLabel = group.behavior === 'all' ? 'AND' : 'OR';
    const logicClass = group.behavior === 'all'
        ? 'view-conditions-modal__logic-badge--and'
        : 'view-conditions-modal__logic-badge--or';
    const indent = depth > 0 ? 'view-conditions-modal__group--nested' : '';

    const node = (
        <div className={`view-conditions-modal__group ${indent}`}>
            <div className="d-flex align-items-center gap-2 mb-2">
                <span className={`view-conditions-modal__logic-badge ${logicClass}`}>{logicLabel}</span>
                <span className="view-conditions-modal__logic-hint">
                    {group.behavior === 'all' ? 'All of the following must be true' : 'At least one must be true'}
                </span>
            </div>
            <div className="d-flex flex-column gap-2">
                {condNodes}
                {childNodes}
            </div>
        </div>
    );

    return { node, nextIdx: idx };
}

// ── Modal ──────────────────────────────────────────────────────────────────────
const ViewConditionsModal = ({ rule, onClose }: ViewConditionsModalProps) => {
    const { t } = useI18n();

    const hasGroups = (rule?.conditionGroups?.length ?? 0) > 0;
    const flatConditions = rule?.conditions ?? [];

    let groupNodes: React.ReactNode[] = [];
    if (hasGroups) {
        let idx = 0;
        groupNodes = (rule!.conditionGroups!).map((g, i) => {
            const result = renderGroup(g, 0, idx, t);
            idx = result.nextIdx;
            return <React.Fragment key={i}>{result.node}</React.Fragment>;
        });
    }

    return (
        <Modal
            show={!!rule}
            onHide={onClose}
            size="lg"
            centered
            contentClassName="view-conditions-modal__content"
        >
            <Modal.Header
                className="view-conditions-modal__header d-flex justify-content-between align-items-center"
                closeButton={false}
            >
                <div className="d-flex align-items-center gap-2">
                    <span className="view-conditions-modal__header-icon">⬡</span>
                    <Typography variant="h6" className="view-conditions-modal__header-title mb-0">
                        {t(VIEW_COND_MODAL_TITLE, 'Rule Conditions')}
                    </Typography>
                    {rule?.id && (
                        <code className="view-conditions-modal__rule-id">{rule.id}</code>
                    )}
                </div>
                <button className="view-conditions-modal__close-btn" onClick={onClose} aria-label="Close">✕</button>
            </Modal.Header>

            <Modal.Body className="view-conditions-modal__body custom-scrollbar">
                <div className="d-flex flex-column gap-3">
                    {hasGroups
                        ? groupNodes
                        : flatConditions.map((c, i) => (
                            <ConditionCard key={i} cond={c} index={i} t={t} />
                        ))
                    }

                    <div className="view-conditions-modal__payload-section">
                        <div className="view-conditions-modal__payload-label">
                            {t(VIEW_COND_RAW_JSON, '</> Raw JSON Payload')}
                        </div>
                        <JSONPretty
                            data={hasGroups ? rule?.conditionGroups : flatConditions}
                            className="view-conditions-modal__payload-json"
                        />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ViewConditionsModal;
